import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import { CardContent, Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { useStoreSelector } from 'v3/data/store';
import { BodySmallRegular } from 'v3/shared/typography/BodySmall';
import { SubtitleLarge } from 'v3/shared/typography/SubtitleLarge';
import { theme } from 'v3/styles/global-styles';
import {
  BodySmallRegularStyled,
  CardHeaderStyled,
  CardStyled,
  Content,
  CustomBodySmallRegular,
  EditIconStyled,
  PersonalContent,
} from '../styles';

import { showErrorDialog } from 'v3/data/store/error/actions';
import * as api from 'v3/services/api';

import { HttpStatusCode, isAxiosError } from 'axios';
import { useParams } from 'react-router-dom';
import { formatCPF } from 'utils/formatters';
import { getPhoneNumberWithoutAreaCode } from 'utils/getPhoneNumberWithoutAreaCode';
import { ContractingStateEnum } from 'v3/data/store/contracting/enums';
import { setHasDimoAccount, setPersonalInformation } from 'v3/data/store/person/actions';
import { IContractingDetailsParams } from 'v3/pages/Contracting/Details/Cards/types';
import { ChangePersonalInformationsModal } from 'v3/shared/components/Modal/ChangePersonalInformationsModal';

export const PersonalInformation = () => {
  const { person, hasDimoAccount } = useStoreSelector((store) => store.person);
  const { contractingKey } = useParams<Readonly<IContractingDetailsParams>>() as IContractingDetailsParams;
  const { contractingState } = useStoreSelector((store) => store.contracting);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getPersonInformation = useCallback(async () => {
    try {
      const responseGetPerson = await api.getPersonalInformation(contractingKey);
      responseGetPerson.data.phoneNumber = getPhoneNumberWithoutAreaCode(responseGetPerson.data.phoneNumber);
      setPersonalInformation(responseGetPerson.data);
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === HttpStatusCode.NotFound) console.error(error);
      } else {
        showErrorDialog('Erro ao obter detalhes do cliente', error);
      }
    }
  }, [contractingKey]);

  const getAccount = async (contractingKey: string) => {
    const response = await api.getAccount(contractingKey);
    setHasDimoAccount(response.data.hasDimoAccount);
  };

  useEffect(() => {
    getAccount(contractingKey);
    return () => {
      setPersonalInformation({
        fullName: '',
        cpf: '',
        phoneNumber: '',
        phoneAreaCode: '',
        email: '',
        address: {
          neighborhood: undefined,
          street: undefined,
          number: undefined,
        },
      });
    };
  }, [contractingKey]);

  const handleEditClick = () => {
    setIsModalOpen(true);
  };

  const isEditIconVisible = () => {
    if (hasDimoAccount) return false;

    const visibleStates = [ContractingStateEnum.WaitingProposalAcceptance, ContractingStateEnum.WaitingTermsAcceptance];

    return visibleStates.includes(contractingState);
  };

  const getPersonalInformation = useCallback(async () => {
    await getPersonInformation();
  }, [contractingState]);

  useEffect(() => {
    getPersonalInformation();
  }, [getPersonalInformation]);

  return (
    <CardStyled>
      <CardHeaderStyled
        title={
          <PersonalContent>
            <Content>
              <AccountBoxOutlinedIcon style={{ color: theme.palette.error.main }} />
              <SubtitleLarge>Informações do cliente</SubtitleLarge>
            </Content>
            {isEditIconVisible() && <EditIconStyled onClick={handleEditClick} />}
          </PersonalContent>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <BodySmallRegularStyled>Nome</BodySmallRegularStyled>
            <BodySmallRegular className="empty-line">{person.fullName}</BodySmallRegular>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <BodySmallRegularStyled>CPF</BodySmallRegularStyled>
            <BodySmallRegular className="empty-line">{formatCPF(person.cpf)}</BodySmallRegular>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <BodySmallRegularStyled>Telefone</BodySmallRegularStyled>
            <BodySmallRegular className="empty-line">
              {person.phoneNumber ? `(${person.phoneAreaCode}) ${person.phoneNumber}` : '-'}
            </BodySmallRegular>
          </Grid>

          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <BodySmallRegularStyled>E-mail</BodySmallRegularStyled>

            <CustomBodySmallRegular className="empty-line">{person.email}</CustomBodySmallRegular>
          </Grid>
        </Grid>
      </CardContent>

      <ChangePersonalInformationsModal active={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </CardStyled>
  );
};
